import React, { useState } from 'react';

const API_KEY = process.env.REACT_APP_CATALOGUE_API_KEY;
const Scan = () => {
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Map common card numbers to their set IDs
  const setIdMap = {
    '4/102': 'base1',
    '102/102': 'base1',
    '1/102': 'base1',
    // Add more mappings as needed
  };

  const [debugInfo, setDebugInfo] = useState({
    lastApiResponse: null,
    searchType: null,
    attemptedSlug: null
  });

  const handleSearch = async () => {
    if (!API_KEY) {
      setError('API key not configured properly');
      return;
    }

    if (!cardName && !cardNumber) {
      setError('Please enter either a card name or number');
      return;
    }

    setLoading(true);
    setError(null);
    setSearchResults(null);

    const trimmedName = cardName.trim();
    const trimmedNumber = cardNumber.trim();

    console.log('Search initiated with:', { 
      cardName: trimmedName, 
      cardNumber: trimmedNumber 
    });

    try {
      // If both name and number are provided, try exact match first
      if (trimmedName && trimmedNumber) {
        const slugGuess = `${trimmedName.toLowerCase().replace(/\s+/g, '-')}-${trimmedNumber.toLowerCase().replace('/', '-')}-holo`;
        setDebugInfo(prev => ({
          ...prev,
          attemptedSlug: slugGuess,
          searchType: 'Exact Match Attempt'
        }));
        console.log('Attempting exact match with slug:', slugGuess);
        
        const lookupResponse = await fetch('https://pokepulse-catalogue-service.replit.app/api/cards/lookup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': API_KEY
          },
          body: JSON.stringify({
            slugs: [slugGuess]
          })
        });

        const lookupData = await lookupResponse.json();
        setDebugInfo(prev => ({
          ...prev,
          lastApiResponse: lookupData
        }));
        console.log('Lookup response:', lookupData);
        
        if (lookupData.cards && lookupData.cards.length > 0) {
          console.log('Exact match found!');
          setSearchResults({
            type: 'exact',
            data: lookupData.cards
          });
      setLoading(false);
          return;
        } else {
          console.log('No exact match found, falling back to search');
        }
      }

// Construct search parameters
      const searchParams = {
        exact_match: true,
        page: 1,
        limit: 10
      };

      if (trimmedName && trimmedNumber) {
        searchParams.query = trimmedName;
        searchParams.card_number = trimmedNumber;
      } else if (trimmedName) {
        searchParams.query = trimmedName;
        // card_number not included
      } else if (trimmedNumber) {
        // query not included
        searchParams.card_number = trimmedNumber;
      }

      console.log('Sending search params:', searchParams);

      const searchResponse = await fetch('https://pokepulse-catalogue-service.replit.app/api/cards/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        body: JSON.stringify(searchParams)
      });

      const searchData = await searchResponse.json();
      console.log('Search response:', searchData);
      
      if (searchData.cards && searchData.cards.length > 0) {
        // Filter results to match criteria more strictly
        let filteredCards = searchData.cards;
        if (trimmedNumber) {
          filteredCards = searchData.cards.filter(card => {
            return card.card_number.toLowerCase() === trimmedNumber.toLowerCase();
          });
          }
        console.log('Filtered to', filteredCards.length, 'results');
        
        if (filteredCards.length > 0) {
          setSearchResults({
            type: 'filtered',
            data: filteredCards,
            originalCount: searchData.cards.length
          });
        } else {
          setError('No cards found matching your exact criteria.');
        }
      } else {
        setError('No cards found matching your search criteria.');
      }

      setDebugInfo(prev => ({
        ...prev,
        lastApiResponse: searchData,
        searchType: cardName && cardNumber ? 'Fallback Search' : 'General Search'
      }));
      console.log('Search response:', searchData);
      
    } catch (err) {
      console.error('Search error:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack
      });
      setError('Failed to fetch card data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Search</h1>
      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="cardName" className="block text-sm font-medium text-gray-700 mb-1">
              Card Name
            </label>
            <input
              type="text"
              id="cardName"
              value={cardName}
              onChange={(e) => setCardName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              placeholder="Enter card name (e.g. Charizard)"
            />
          </div>
          <div>
            <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Card Number
            </label>
            <input
              type="text"
              id="cardNumber"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              placeholder="Enter card number (e.g. 4/102)"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="w-full bg-black hover:bg-gray-800 text-white px-4 py-2 rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
          {error && (
            <div className="text-red-600 mt-4">
              {error}
            </div>
          )}
          {searchResults && (
            <div className="mt-6">
              <h2 className="text-xl font-semibold mb-4">
                {searchResults.type === 'exact' ? 'Exact Match Found' :
                  searchResults.type === 'number' ? 'Cards with Number ' + cardNumber :
                    'Search Results'}
              </h2>
              <div className="space-y-4">
                {searchResults.data.map((card) => (
                  <div key={card.id} className="border rounded-lg p-4">
                    <div className="flex items-start">
                      {card.thumbnail_url && (
                        <img
                          src={card.thumbnail_url}
                          alt={card.card_name}
                          className="w-24 h-auto rounded-lg mr-4"
                        />
                      )}
                      <div>
                        <h3 className="font-bold">{card.card_name}</h3>
                        <p className="text-sm text-gray-600">Number: {card.card_number}</p>
                        <p className="text-sm text-gray-600">Rarity: {card.rarity}</p>
                        <p className="text-sm text-gray-600">Set: {card.set_id}</p>
                        {card.material && (
                          <p className="text-sm text-gray-600">Material: {card.material}</p>
                        )}
                        {card.ebay_url && (
                          <a 
                            href={card.ebay_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm block mt-2"
                          >
                            View on eBay
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Debug Information Panel */}
          <div className="mt-8 p-4 bg-gray-100 rounded-lg">
            <h3 className="text-lg font-semibold mb-2">Debug Information</h3>
            <div className="mb-4 p-2 bg-yellow-100 rounded">
              <p className="font-semibold">Search Parameters:</p>
              <pre className="bg-yellow-50 p-2 mt-1 rounded overflow-auto max-h-24">
                {JSON.stringify({
                  query: cardName || '',
                  card_number: cardNumber || '',
                  set_id: setIdMap[cardNumber] || '',
                  exact_match: true
                }, null, 2)}
              </pre>
              {searchResults && (
                <div className="mt-2">
                  <p className="font-semibold">Results Summary:</p>
                  <ul className="list-disc list-inside">
                    <li>Total results before filtering: {searchResults.originalCount || 0}</li>
                    <li>Filtered results: {searchResults.data.length}</li>
                    <li>Search type: {searchResults.type}</li>
                    <li>Set ID used: {setIdMap[cardNumber] || 'none'}</li>
                  </ul>
                </div>
              )}
            </div>
            <div className="space-y-2 font-mono text-sm">
              <div>
                <strong>Search Type:</strong> {debugInfo.searchType || 'None'}
              </div>
              {debugInfo.attemptedSlug && (
                <div>
                  <strong>Attempted Slug:</strong> {debugInfo.attemptedSlug}
                </div>
              )}
              <div>
                <strong>API Response:</strong>
                <pre className="bg-gray-200 p-2 mt-1 rounded overflow-auto max-h-48">
                  {JSON.stringify(debugInfo.lastApiResponse, null, 2)}
                </pre>
              </div>
              <div>
                <strong>Current State:</strong>
                <pre className="bg-gray-200 p-2 mt-1 rounded overflow-auto max-h-48">
                  {JSON.stringify({
                    cardName,
                    cardNumber,
                    loading,
                    error,
                    hasResults: searchResults !== null
                  }, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scan;